// components/BoxCard.js - Updated with latest measurements preview
import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import ScoreIcon from '@mui/icons-material/Score';
import SensorsIcon from '@mui/icons-material/Sensors';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useTheme } from '@mui/material/styles';
import { getStatusColor, getScoreColor, formatDate } from '../utils/helpers';
import { useLatestMeasurements } from '../hooks/useMeasurements';

export default function BoxCard({ box, onBoxClick }) {
  const theme = useTheme();
  const statusProps = getStatusColor(box.status);
  const scoreColor = getScoreColor(box.score);

  // Fetch latest measurements for preview
  const { data: measurementData, loading: measurementsLoading } = useLatestMeasurements(box.box_id, {
    retry: false, // Don't retry automatically to avoid overwhelming the API
    errorBoundary: false // Handle errors gracefully without breaking the card
  });

  // Count active sensors
  const activeSensorCount = measurementData?.measurement_data
    ? Object.entries(measurementData.measurement_data).filter(([name, data]) =>
        data !== null && data !== undefined && Object.keys(data).length > 0
      ).length
    : 0;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        }
      }}
      onClick={() => onBoxClick(box)}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              {box.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              ID: {box.box_id}
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => e.stopPropagation()}>
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 20 }}>
          {box.description !== 'N/A' ? box.description : 'No description available'}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {box.location}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {box.assigned_to}
            </Typography>
          </Box>

          {/* Sensor count with live data */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SensorsIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {measurementsLoading ? (
                'Loading sensors...'
              ) : measurementData ? (
                <Tooltip title={`${activeSensorCount} active sensors detected`}>
                  <span>
                    {activeSensorCount} active sensor{activeSensorCount !== 1 ? 's' : ''}
                  </span>
                </Tooltip>
              ) : (
                'Sensor data unavailable'
              )}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Chip
              label={box.status.toUpperCase()}
              size="small"
              sx={{
                ...statusProps,
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScoreIcon sx={{ fontSize: 16, color: `${scoreColor}.main` }} />
              <Typography variant="body2" sx={{ color: `${scoreColor}.main`, fontWeight: 600 }}>
                {box.score}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Updated: {formatDate(box.updated_at)}
          </Typography>
          {measurementData?.timestamp && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Latest measurement: {formatDate(measurementData.timestamp)}
            </Typography>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ pt: 0 }}>
        <Button
          size="small"
          variant="contained"
          endIcon={<ChevronRightRoundedIcon />}
          fullWidth
          onClick={(e) => {
            e.stopPropagation();
            onBoxClick(box);
          }}
        >
          View Sensors {activeSensorCount > 0 && `(${activeSensorCount})`}
        </Button>
      </CardActions>
    </Card>
  );
}